/**
 * @file src/pages/invoice-management-hub/components/StatusBadge.jsx
 * @description Status pill for invoice rows.
 *
 * Pure presentational component — no state, no side effects.
 * Renders a coloured pill based on invoice status / clearance status,
 * plus an optional "Signed" pill when the invoice has a signature.
 */

import React from "react";
import Icon from "components/AppIcon";

const STATUS_CONFIG = {
  DRAFT: {
    color: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
    icon: "Edit3",
    text: "Draft",
  },
  ISSUED: {
    color: "bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400",
    icon: "Clock",
    text: "Issued",
  },
  REPORTED: {
    color:
      "bg-purple-100 text-purple-700 dark:bg-purple-900/20 dark:text-purple-400",
    icon: "CheckCircle2",
    text: "Reported",
  },
  CLEARED: {
    color:
      "bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400",
    icon: "CheckCircle2",
    text: "Cleared",
  },
  REJECTED: {
    color: "bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400",
    icon: "X",
    text: "Rejected",
  },
  VOID: {
    color: "bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400",
    icon: "Ban",
    text: "Void",
  },
};

const FALLBACK_CONFIG = {
  color: "bg-gray-100 text-gray-700 dark:bg-gray-900/20 dark:text-gray-400",
  icon: "FileText",
};

const StatusBadge = ({ status, clearanceStatus, hasSignature }) => {
  // Clearance-cleared overrides the raw status pill
  const effectiveStatus = clearanceStatus === "CLEARED" ? "CLEARED" : status;

  const config = STATUS_CONFIG[effectiveStatus] ?? {
    ...FALLBACK_CONFIG,
    text: status || "Unknown",
  };

  return (
    <div className="flex flex-col gap-1">
      <span
        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}
      >
        <Icon name={config.icon} size={12} />
        {config.text}
      </span>
      {hasSignature && (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/20 dark:text-purple-400">
          <Icon name="Shield" size={12} />
          Signed
        </span>
      )}
    </div>
  );
};

const PAYMENT_STATUS_CONFIG = {
  UNPAID: {
    color:
      "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-300",
    icon: "Clock",
    text: "Unpaid",
  },
  PARTIAL: {
    color: "bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300",
    icon: "PieChart",
    text: "Partial",
  },
  PAID: {
    color:
      "bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-300",
    icon: "CheckCircle2",
    text: "Paid",
  },
  OVERDUE: {
    color: "bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-300",
    icon: "AlertTriangle",
    text: "Overdue",
  },
  CREDITED: {
    color:
      "bg-purple-100 text-purple-700 dark:bg-purple-900/20 dark:text-purple-300",
    icon: "BadgeCheck",
    text: "Credited",
  },
  VOID: {
    color: "bg-gray-100 text-gray-700 dark:bg-gray-900/20 dark:text-gray-400",
    icon: "Ban",
    text: "Void",
  },
};

export const PaymentStatusBadge = ({ status }) => {
  const config = PAYMENT_STATUS_CONFIG[status] || {
    color: "bg-gray-100 text-gray-700 dark:bg-gray-900/20 dark:text-gray-400",
    icon: "Circle",
    text: status || "Unknown",
  };

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}
    >
      <Icon name={config.icon} size={12} />
      {config.text}
    </span>
  );
};

export default StatusBadge;
